import { useEffect, useState, useCallback, useMemo } from 'react';
import TableComponent from './components/tableComponent';
import UserFormModal from './components/createUserForm';
import { useDashboard } from '../../../context/dashboardContext';
import { LoadingModal } from '../../../components/shareable/loadingModal';
import { useSearch } from '../../../context/SearchContext';
import toast from 'react-hot-toast';

const Home = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { allUsers } = useDashboard();
  const [allUsersData, setAllUsersData] = useState([]);
  const [modelOpen, setModelOpen] = useState(false);
  const { searchQuery, updateSearch } = useSearch();

  // Set the page in search context when component mounts
  useEffect(() => {
    updateSearch('', 'home');
    
    return () => updateSearch('', '');
  }, [updateSearch]);

  // Fetch all users data (only once on mount)
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await allUsers();
      if (result.success) {
        setAllUsersData(result.data);
      } else {
        toast.error('Failed to load the Users.');
      }
    } catch (err) {
      toast.error('An error occurred while loading the Users.');
    } finally {
      setLoading(false);
    }
  }, [allUsers]);

  // Fetch data on component mount
  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  // Filter users based on search query (client-side filtering)
  const filteredUsers = useMemo(() => {
    if (!searchQuery) { return allUsersData; }
    
    return allUsersData.filter(user => 
      user.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [allUsersData, searchQuery]);

  // Transform filtered data for table
  const tableData = useMemo(() => {
    return filteredUsers.map((item) => ({
      Client: item.name,
      Email: item.email,
      Date: new Date(item.createdAt).toLocaleDateString(),
      id: item._id,
    }));
  }, [filteredUsers]);

  const closeModel = () => setModelOpen(false);

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="relative mb-4 mt-3 flex justify-between items-center">
        <h2 className="text-2xl font-bold text-red-700 uppercase tracking-wide">
          Users {searchQuery && `(Filtered: ${filteredUsers.length} found)`}
        </h2>
        <div className="flex flex-col items-end">
          <button onClick={() => setModelOpen(true)} className="mt-3 sm:mt-0 px-4 py-2 text-sm font-medium bg-gradient-to-r from-red-600 to-red-800 text-white rounded-lg shadow hover:shadow-md hover:scale-[1.02] transition-all duration-200" >
            Create New User
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-md shadow-sm">
          {error}
        </div>
      )}

      {/* Table */}
      <div className="bg-white shadow-md rounded-lg overflow-hidden">
        <TableComponent data={tableData} refreshData={fetchUsers} />
      </div>

      {/* Modals */}
      <UserFormModal isOpen={modelOpen} onClose={closeModel} refreshData={fetchUsers} />
      <LoadingModal loading={loading} />
    </div>
  );
};

export default Home;